import { useEffect, useState } from 'react'
import { api } from '../api'
import FactureComModal from '../components/marche/FactureComModal'
import InvoicePreviewModal from '../components/InvoicePreviewModal'
import RinaContextCard from '../components/RinaContextCard'
import { ORANGE, VERT, eur, fmtDateCourte } from '../components/marche/marcheProducer'
import { useActionsPortail } from '../contexts/navigation'
import PageHub from '../components/PageHub'

/**
 * « Ma facturation » — page GLOBALE tous modules (proto v7.2.3, Compte >
 * Ma facturation). Vue de consultation : bandeau « N factures en attente
 * d'action », filtre par module (Tous / Marche / Paniers), lignes derivees
 * des donnees reelles (jamais de copie) :
 * - Marche : participations de l'historique (source unique) — une ligne
 *   actionnable NAVIGUE vers Marche > Historique (la gestion reste au module),
 *   une ligne reglee ouvre la facture COM plein ecran (FactureComModal).
 * - Paniers : pieces ModuleInvoice TOURNEES (versements) — PDF direct.
 * - Anciennes factures FAC (serie close, arbitrage Enzo 4 juil) : consultables
 *   ici avec leur numero d'origine, apercu via InvoicePreviewModal.
 */

const STATUTS = {
  ca_a_saisir: { l: 'CA à déclarer', c: '#b8860b', bg: 'rgba(184,134,11,0.12)' },
  a_regler: { l: 'À régler', c: '#c2533f', bg: 'rgba(194,83,63,0.12)' },
  attente: { l: 'En attente Manrina', c: ORANGE, bg: 'rgba(244,137,83,0.14)' },
  regle: { l: 'Réglée', c: '#0a8a5f', bg: 'rgba(10,138,95,0.12)' },
  recu: { l: 'Reçu', c: '#0a8a5f', bg: 'rgba(10,138,95,0.12)' },
  emise: { l: 'Émise', c: '#4b4fb0', bg: 'rgba(75,79,176,0.12)' },
}

const statutMarche = (p) =>
  p.ca == null
    ? 'ca_a_saisir'
    : p.paye
      ? 'regle'
      : p.modePaiement && ['VIREMENT', 'ESPECES'].includes(p.modePaiement)
        ? 'attente'
        : 'a_regler'

function ContenuFacturation() {
  const { ouvrirMarcheProducteur } = useActionsPortail()
  // Navigation croisee v7.2.3 : une facture actionnable ouvre Historique > detail
  const onOpenMarcheHisto = (participationId) => ouvrirMarcheProducteur({ tab: 'historique', histoSel: participationId || null })
  const [histo, setHisto] = useState([])
  const [moduleInvoices, setModuleInvoices] = useState([])
  const [legacy, setLegacy] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtre, setFiltre] = useState('tous') // tous | marche | paniers
  const [factureVue, setFactureVue] = useState(null) // { facture, eventNom, eventDate, enAttenteManrina }
  const [previewLegacy, setPreviewLegacy] = useState(null) // facture FAC (apercu)
  const [flash, setFlash] = useState(null)
  const toast = (msg) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 2800)
  }

  useEffect(() => {
    // Trois sources tolerantes : un module en erreur ne vide pas la page.
    Promise.all([
      api.producer.getHistory().catch(() => []),
      api.producer.getModuleInvoices().catch(() => []),
      api.producer.getInvoices().catch(() => []),
    ])
      .then(([h, mi, inv]) => {
        setHisto(Array.isArray(h) ? h : [])
        setModuleInvoices(Array.isArray(mi) ? mi : [])
        setLegacy(Array.isArray(inv) ? inv : [])
      })
      .finally(() => setLoading(false))
  }, [])

  // ----- Lignes MARCHE : derivees de l'historique reel (source unique) -----
  const lignesMarche = histo.map((p) => {
    const marche = p.marche || p.event || {}
    const f = p.factureCommission
    const statut = statutMarche(p)
    return {
      key: `m-${p.id}`,
      module: 'marche',
      ico: '🥕',
      // Reglee AVANT l'ere facturation (pas de piece COM — le rattrapage ne
      // couvre que les non-reglees) : ne jamais titrer « a regler » une reglee.
      titre: f
        ? `Facture ${f.numero}`
        : statut === 'ca_a_saisir'
          ? 'CA à déclarer'
          : statut === 'regle'
            ? 'Commission réglée'
            : 'Commission à régler',
      sous: `${marche.nom || marche.lieu || 'Évènement'} · ${fmtDateCourte(marche.date)}${p.ca != null ? ` · CA ${eur(p.ca)}` : ''}`,
      montant: f ? Number(f.montantTTC) : p.ca != null ? Number(p.montantCommission) : null,
      statut,
      action: statut === 'ca_a_saisir' || statut === 'a_regler',
      nav: () => onOpenMarcheHisto?.(p.id),
      voir: statut === 'regle' && f
        ? () => setFactureVue({ facture: f, eventNom: marche.nom || marche.lieu, eventDate: marche.date, enAttenteManrina: false })
        : null,
    }
  })

  // ----- Lignes PANIERS : pieces ModuleInvoice TOURNEES (versements) -----
  const lignesPaniers = moduleInvoices
    .filter((f) => f.moduleCode === 'TOURNEES')
    .map((f) => ({
      key: `p-${f.id}`,
      module: 'paniers',
      ico: '🧺',
      titre: 'Versement Paniers',
      sous: `${f.numero} · ${fmtDateCourte(f.dateEmission)}`,
      montant: f.netPayout != null ? Number(f.netPayout) : Number(f.montantTTC),
      statut: 'recu',
      action: false,
      pdf: () =>
        api.producer.downloadModuleInvoicePdf(f.id)
          .then((blob) => {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${f.numero}.pdf`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            setTimeout(() => URL.revokeObjectURL(url), 4000)
          })
          .catch((e) => toast(e.message || 'Le téléchargement du PDF a échoué.')),
    }))

  // ----- Anciennes factures FAC (serie close — consultation seule) -----
  const lignesLegacy = legacy.map((inv) => {
    const marche = inv.participation?.marche || inv.participation?.event
    return {
      key: `l-${inv.id}`,
      module: 'marche',
      ico: '🥕',
      titre: `Facture ${inv.numero}`,
      sous: `${marche ? `${marche.lieu || 'Évènement'} · ${fmtDateCourte(marche.date)} · ` : ''}ancienne série`,
      montant: Number(inv.montantTTC),
      statut: inv.statut === 'PAYEE' ? 'regle' : 'emise',
      action: false,
      voir: () => setPreviewLegacy(inv),
    }
  })

  const toutes = [...lignesMarche, ...lignesPaniers, ...lignesLegacy]
  const visibles = toutes.filter((l) => filtre === 'tous' || l.module === filtre)
  const nbAction = toutes.filter((l) => l.action).length

  return (
    <>
      {/* v7.2.4 : titre + message d'action dans la bulle RINA (plus de titre
          in-page ni de bandeau) ; le retour « ← Compte » vit dans le header. */}
      <RinaContextCard
       
        title="Ma facturation"
        message={nbAction > 0
          ? `${nbAction} facture${nbAction > 1 ? 's' : ''} attend${nbAction > 1 ? 'ent' : ''} une action de votre part — touchez « Gérer › ». Tous vos modules sont réunis ici.`
          : 'Toutes vos factures, tous modules, sont réunies ici. La gestion se fait dans chaque module — un appui vous y emmène.'}
      />

      {loading ? (
        <div className="space-y-2.5">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 rounded-[18px] animate-pulse bg-ink/5" />)}
        </div>
      ) : (
        <>
          {/* Filtre par module — meme patron que « Mes offres » */}
          <div className="flex gap-[7px] mb-3.5">
            {[['tous', 'Tous'], ['marche', '🥕 Marché'], ['paniers', '🧺 Paniers']].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFiltre(k)}
                className="rounded-full text-[12.5px] font-bold"
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  background: filtre === k ? VERT : 'rgba(255,255,255,0.6)',
                  color: filtre === k ? 'rgb(var(--c-creme))' : 'rgba(7,62,61,0.6)',
                }}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            {visibles.map((l) => {
              const st = STATUTS[l.statut]
              const inner = (
                <>
                  <span
                    className="w-10 h-10 rounded-[13px] flex-shrink-0 flex items-center justify-center text-[19px]"
                    style={{ background: 'rgba(20,184,166,0.1)', border: '1px solid rgba(255,255,255,0.7)' }}
                  >
                    {l.ico}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-bold" style={{ color: VERT }}>{l.titre}</p>
                    <p className="text-[11px] mt-px text-ink/50">{l.sous}</p>
                    <span
                      className="inline-block mt-[5px] rounded-full text-[10px] font-extrabold"
                      style={{ padding: '3px 9px', color: st.c, background: st.bg }}
                    >
                      {st.l}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {l.montant != null && <p className="font-serif text-base" style={{ color: VERT }}>{eur(l.montant)}</p>}
                    {l.action && <p className="text-[11px] font-extrabold mt-[3px]" style={{ color: ORANGE }}>Gérer ›</p>}
                    {!l.action && (l.voir || l.pdf) && (
                      <button
                        onClick={l.voir || l.pdf}
                        className="mt-[3px] bg-transparent border-none text-[11px] font-extrabold"
                        style={{ color: ORANGE }}
                      >
                        {l.voir ? '🧾 Voir ›' : '📄 PDF'}
                      </button>
                    )}
                  </div>
                </>
              )
              return l.action ? (
                <button
                  key={l.key}
                  onClick={l.nav}
                  className="glass-surface rounded-[18px] flex items-center gap-3 text-left w-full"
                  style={{ padding: '13px 15px', border: '1px solid rgba(244,137,83,0.35)' }}
                >
                  {inner}
                </button>
              ) : (
                <div key={l.key} className="glass-surface rounded-[18px] flex items-center gap-3" style={{ padding: '13px 15px' }}>
                  {inner}
                </div>
              )
            })}
            {visibles.length === 0 && (
              <div className="glass-surface rounded-2xl p-[18px] text-center text-[13px] text-ink/55">
                Aucune facture sur ce module pour l'instant.
              </div>
            )}
          </div>

          <p className="text-[11px] leading-relaxed text-ink/50" style={{ margin: '14px 2px 0' }}>
            Le prix que vous fixez est sanctuarisé. La commission Manrina est toujours affichée et facturée séparément
            (série COM-2026).
          </p>
        </>
      )}

      {/* Facture COM plein ecran (snapshot ModuleInvoice, proto v7.2.3) */}
      {factureVue && (
        <FactureComModal
          facture={factureVue.facture}
          eventNom={factureVue.eventNom}
          eventDate={factureVue.eventDate}
          enAttenteManrina={factureVue.enAttenteManrina}
          onClose={() => setFactureVue(null)}
          onToast={toast}
        />
      )}

      {/* Apercu d'une ancienne facture FAC (serie close) */}
      {previewLegacy && (
        <InvoicePreviewModal
          invoiceId={previewLegacy.id}
          invoiceNumero={previewLegacy.numero}
          onClose={() => setPreviewLegacy(null)}
        />
      )}

      {flash && (
        <div
          className="glass-dark fixed left-1/2 -translate-x-1/2 rounded-[14px] text-[13px] font-semibold text-center z-[80]"
          style={{ bottom: 90, padding: '11px 18px', maxWidth: 340, color: 'rgb(var(--c-creme))' }}
        >
          {flash}
        </div>
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageFacturation() {
  return <PageHub retour="account"><ContenuFacturation /></PageHub>
}

